import { RxWithTherapistsDto } from "../../../../api-client";
import { useEffect, useState, useMemo } from "react";
import useStore from "../../../../helpers/useStore";
import { BIContext } from "../../../../stores/BIDashboard/biDashboard.provider";
import { useDebounce } from "../../../Transactions/Insurance/Base/hooks/useDebounce";

const useRxBlankoWithTherapistsCounts = (startDate: Date, endDate: Date, locationIds?: string[], therapistCountFilter?: number[] | 'all') => {
  const BIStore = useStore(BIContext);
  const { getRxBlankoWithTherapistsStats } = BIStore;

  // Debounce date changes
  const debounceStartDate = useDebounce(startDate, 300);
  const debounceEndDate = useDebounce(endDate, 300);

  // Memoize locationIds to avoid unnecessary re-renders
  const memoizedLocationIds = useMemo(() => {
    if (!locationIds || locationIds.length === 0) {
      return undefined;
    }
    return locationIds;
  }, [locationIds?.join(','), locationIds?.length]);

  const [allRxBlankoWithTherapists, setAllRxBlankoWithTherapists] = useState<RxWithTherapistsDto[]>([]);
  const [rxBlankoWithTherapists, setRxBlankoWithTherapists] = useState<RxWithTherapistsDto[]>([]);
  const [filteredRxBlankoWithTherapists, setFilteredRxBlankoWithTherapists] = useState<RxWithTherapistsDto[]>([]);
  const [availableTherapistCounts, setAvailableTherapistCounts] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        let response: RxWithTherapistsDto[] | undefined;

        if (debounceStartDate && debounceEndDate) {
          // console.log(`[Rx Blanko Therapist Count] Fetching data for date range: ${debounceStartDate} - ${debounceEndDate}`);
          // Always fetch all locations data, don't pass location filter to backend
          response = await getRxBlankoWithTherapistsStats(debounceStartDate, debounceEndDate);
          // console.log("[Rx Blanko Therapist Count] Backend responded with:", response);
        } else {
          // console.log("[Rx Blanko Therapist Count] Fetching all data");
        }

        if (response && response.length > 0) {
          setAllRxBlankoWithTherapists(response);
          
          // Apply location filter immediately when setting data
          if (memoizedLocationIds && memoizedLocationIds.length > 0) {
            setRxBlankoWithTherapists(response.filter(p => memoizedLocationIds.includes(p.locationId)));
          } else {
            setRxBlankoWithTherapists(response);
          }
          
          // Calculate available therapist counts
          const uniqueCounts = [...new Set(response.map(item => item.therapistCount))].sort((a, b) => a - b);
          setAvailableTherapistCounts(uniqueCounts);
        } else {
          // console.log("[Rx Blanko Therapist Count] No data found for the specified range.");
          setAllRxBlankoWithTherapists([]);
          setRxBlankoWithTherapists([]);
        }
      } catch (error) {
        // console.log("[Rx Blanko Therapist Count] Error fetching Blanko Rx with therapist counts", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [debounceStartDate, debounceEndDate, getRxBlankoWithTherapistsStats]);

  // Filter locally whenever locations change (but not when data is initially loaded)
  useEffect(() => {
    if (!isLoading && allRxBlankoWithTherapists.length > 0) {
      if (memoizedLocationIds && memoizedLocationIds.length > 0) {
        setRxBlankoWithTherapists(allRxBlankoWithTherapists.filter(p => memoizedLocationIds.includes(p.locationId)));
      } else {
        // No location selected → show all
        setRxBlankoWithTherapists(allRxBlankoWithTherapists);
      }
    }
  }, [memoizedLocationIds, allRxBlankoWithTherapists, isLoading]);

  // Filter therapist stats based on therapist count filter
  useEffect(() => {
    if (therapistCountFilter === 'all' || therapistCountFilter === undefined || therapistCountFilter.length === 0) {
      setFilteredRxBlankoWithTherapists(rxBlankoWithTherapists);
    } else {
      const filtered = rxBlankoWithTherapists.filter(stat => therapistCountFilter.includes(stat.therapistCount));
      setFilteredRxBlankoWithTherapists(filtered);
    }
  }, [rxBlankoWithTherapists, therapistCountFilter]);

  const availableLocations = useMemo(() => {
    const locMap = new Map<string, string>();
    allRxBlankoWithTherapists.forEach((p) => {
      if (p.locationId) locMap.set(p.locationId, p.locationId);
    });
    return Array.from(locMap.entries()).map(([id, name]) => ({ id, name }));
  }, [allRxBlankoWithTherapists]);

  return { rxBlankoWithTherapists, filteredRxBlankoWithTherapists, availableTherapistCounts, isLoading, availableLocations };
};

export default useRxBlankoWithTherapistsCounts;